import createFetchClientBase, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./api.types";

export type ApiErrorResponse = components["schemas"]["ErrorEnvelope"];

/**
 * ApiError is thrown for any non-ok response. It carries the parsed error
 * envelope so callers can branch on error.error.code instead of parsing the
 * status text. It satisfies ApiErrorResponse so existing `error as
 * ApiErrorResponse` reads keep working.
 */
export class ApiError extends Error implements ApiErrorResponse {
  readonly success = false;
  readonly status: number;
  readonly error?: components["schemas"]["ErrorBody"];

  constructor(args: {
    status: number;
    statusText: string;
    url: string;
    body?: ApiErrorResponse;
  }) {
    super(
      args.body?.error?.message ??
        `${args.url}: ${args.status} ${args.statusText}`,
    );
    this.name = "ApiError";
    this.status = args.status;
    this.error = args.body?.error;
  }
}

async function readErrorBody(
  response: Response,
): Promise<ApiErrorResponse | undefined> {
  try {
    const data = await response.clone().json();
    if (data && typeof data === "object" && "error" in data) {
      return data as ApiErrorResponse;
    }
  } catch {
    // body wasn't json (e.g. a gateway html page), nothing to attach
  }
  return undefined;
}

export type CreateFetchClientOptions = {
  baseUrl: string;
  getToken: () => string | undefined | null;
  onUnauthorized?: () => void | Promise<void>;
  onResponseError?: (
    error: Error,
    context: {
      url: string;
      method: string;
      status: number;
      statusText: string;
    },
  ) => void;
};

/**
 * Creates an authenticated fetch client for the API.
 * Each app provides getToken (e.g. from Frontegg or Expo) and onUnauthorized (e.g. signOut).
 */
export function createFetchClient<P extends paths>({
  baseUrl,
  getToken,
  onUnauthorized,
  onResponseError,
}: CreateFetchClientOptions) {
  const client = createFetchClientBase<P>({ baseUrl });

  const middleware: Middleware = {
    async onRequest({ request }) {
      const token = getToken();
      if (token && !request.headers.has("Authorization")) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    },
    async onResponse({ response, request }) {
      if (response.status === 401 && onUnauthorized) {
        await onUnauthorized();
      }

      if (!response.ok) {
        const body = await readErrorBody(response);
        const error = new ApiError({
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          body,
        });
        if (onResponseError) {
          onResponseError(error, {
            url: response.url,
            method: request.method,
            status: response.status,
            statusText: response.statusText,
          });
        }
        throw error;
      }
    },
  };

  client.use(middleware);
  return client;
}

/**
 * Extracts a user-facing error message from an unknown error.
 * @param fallback - Used when message cannot be extracted (e.g. i18n.t("common_error"))
 */
export function getErrorMessage(
  error: unknown,
  fallback = "An error occurred",
): string {
  const isObject = error && typeof error === "object";

  if (isObject && "error" in error) {
    const apiError = error as ApiErrorResponse;
    if (apiError?.error?.message) {
      return apiError.error.message || fallback;
    }
  }

  if (isObject && "message" in error && typeof error.message === "string") {
    return error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message || fallback;
  }

  return typeof error === "string" ? error : fallback;
}
